import React, { useState } from 'react';
import { supabase, isSupabaseConfigured } from '../src/lib/supabaseClient';
import { getPasswordResetRedirectTo } from '../utils/passwordResetRedirect';
import type { AuthLang } from './AuthModal';

interface ForgotPasswordProps {
  language?: AuthLang;
  isDarkMode?: boolean;
  onBackToLogin: () => void;
}

const copy: Record<
  AuthLang,
  {
    title: string;
    desc: string;
    labelEmail: string;
    phEmail: string;
    submit: string;
    submitting: string;
    back: string;
    enterEmail: string;
    sent: string;
    afterSentNote: string;
    error: (e: string) => string;
    notConfigured: string;
  }
> = {
  en: {
    title: 'Reset password',
    desc: 'Enter your registered email. We will send a link to set a new password.',
    labelEmail: 'EMAIL',
    phEmail: 'name@domain.com',
    submit: 'Send reset link',
    submitting: 'Sending…',
    back: 'Back to sign in',
    enterEmail: 'Please enter your email.',
    sent: 'If this email is registered, you will receive a message shortly. Check your inbox and spam folder.',
    afterSentNote:
      'If you open the link inside WeChat or QQ Mail, you may see a warning such as “visiting an external webpage”. That is normal: the mail app is asking you to confirm opening Supabase’s secure link (not part of Nemo Light). Tap Continue, then you will return here to set a new password.',
    error: (e) => e || 'Could not send reset email.',
    notConfigured: 'Supabase is not configured.',
  },
  zh: {
    title: '忘记密码',
    desc: '填写注册时使用的邮箱，我们将发送设置新密码的链接到该邮箱。',
    labelEmail: '邮箱',
    phEmail: 'name@domain.com',
    submit: '发送重置链接',
    submitting: '发送中…',
    back: '返回登录',
    enterEmail: '请输入邮箱。',
    sent: '若该邮箱已注册，您将很快收到邮件，请查收收件箱与垃圾箱。',
    afterSentNote:
      '在微信、QQ 邮箱等里点开邮件里的重置链接时，可能会出现「将要访问外部网页」之类的提示。这是邮箱或微信在提醒您：接下来要打开的是**验证链接所在的网站**（Supabase，负责安全登录的服务），不是病毒，也不是程序坏了。点「继续访问」即可，随后会回到本实验室并出现「设置新密码」页面。',
    error: (e) => e || '发送失败，请稍后重试。',
    notConfigured: '未配置 Supabase，无法发送邮件。',
  },
};

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ language = 'zh', isDarkMode = true, onBackToLogin }) => {
  const t = copy[language] ?? copy.zh;
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);

  const accent = '#D7CDA1';
  const bg = isDarkMode ? '#000000' : '#fafafa';
  const text = isDarkMode ? '#fff' : '#111';
  const muted = isDarkMode ? '#888' : '#666';
  const border = isDarkMode ? '#333' : '#e5e5e5';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    if (!email.trim()) {
      setMessage(t.enterEmail);
      return;
    }
    if (!isSupabaseConfigured()) {
      setMessage(t.notConfigured);
      return;
    }
    setLoading(true);
    try {
      const redirectTo = getPasswordResetRedirectTo();
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
        redirectTo,
      });
      if (error) throw error;
      setSuccess(true);
      setMessage(t.sent);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setMessage(t.error(msg));
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-full flex flex-col items-center justify-center px-6 py-12"
      style={{ background: bg, color: text, fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <div className="w-full max-w-md">
        <button
          type="button"
          onClick={onBackToLogin}
          className="mb-6 text-sm transition-opacity hover:opacity-80"
          style={{ color: muted }}
        >
          ← {t.back}
        </button>
        <h2 className="text-2xl font-bold mb-2" style={{ color: text }}>
          {t.title}
        </h2>
        <p className="text-sm mb-8 leading-relaxed" style={{ color: muted }}>
          {t.desc}
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-xs tracking-wide mb-2" style={{ color: muted }}>
              {t.labelEmail}
            </label>
            <input
              type="email"
              required
              autoComplete="email"
              disabled={success}
              placeholder={t.phEmail}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b py-2 outline-none focus:border-opacity-100 transition-colors"
              style={{ borderColor: border, color: text }}
            />
          </div>

          {message ? (
            <div
              className="text-sm rounded-lg px-3 py-2 border"
              style={
                success
                  ? isDarkMode
                    ? { borderColor: '#2d5a2d', background: 'rgba(34,80,34,0.35)', color: '#86efac' }
                    : { borderColor: '#6ee7b7', background: '#ecfdf5', color: '#047857' }
                  : isDarkMode
                    ? { borderColor: '#5a2d2d', background: 'rgba(80,34,34,0.35)', color: '#fca5a5' }
                    : { borderColor: '#fecaca', background: '#fef2f2', color: '#b91c1c' }
              }
            >
              <div>{message}</div>
              {success ? (
                <p className="mt-3 pt-3 text-xs leading-relaxed border-t border-current opacity-80">
                  {t.afterSentNote}
                </p>
              ) : null}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={loading || success}
            className="w-full py-3.5 rounded-xl font-semibold text-black transition-opacity disabled:opacity-50"
            style={{ background: accent }}
          >
            {loading ? t.submitting : t.submit}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
